/**
 * Form primitives.
 *
 * Every field wires its label, help text and error together with real ids and
 * `aria-describedby`, and errors carry `role="alert"`. WCAG 2.2 AA is a P7
 * gate (TC-039), and retrofitting accessible error handling onto forms built
 * without it is far more work than building it in once here.
 */
import type {
  CSSProperties, InputHTMLAttributes, ReactNode, SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { useId } from "react";
import { fontSize, radius } from "@design/index";

const controlStyle = (invalid: boolean): CSSProperties => ({
  width: "100%",
  padding: "10px 12px",
  fontSize: fontSize.bodyLg,
  color: "var(--text)",
  background: "var(--surface-2)",
  border: `1px solid ${invalid ? "var(--danger-bg)" : "var(--line-strong)"}`,
  borderRadius: radius.control,
  outline: "none",
});

export function FieldTip({ text }: { text: string }) {
  return (
    <span className="cm-field-tip">
      <span className="cm-field-tip-icon" tabIndex={0} aria-label={text}>i</span>
      <span className="cm-field-tip-bubble" role="tooltip">{text}</span>
    </span>
  );
}

export function Field({
  label, error, hint, required, children,
}: {
  label: string; error?: string | undefined; hint?: string;
  required?: boolean;
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errId = `${id}-err`;
  const describedBy = [hint ? hintId : null, error ? errId : null].filter(Boolean).join(" ") || undefined;

  return (
    <div style={{ display: "grid", gap: 6 }}>
      <label htmlFor={id} style={{ fontSize: fontSize.body, fontWeight: 600, color: "var(--text-2)" }}>
        {label}
        {required ? <span aria-hidden="true" style={{ color: "var(--accent)" }}> *</span> : null}
        {!required ? <span style={{ color: "var(--faint)", fontWeight: 400 }}> (optional)</span> : null}
        {hint ? <FieldTip text={hint} /> : null}
      </label>
      {hint ? <span id={hintId} className="cm-sr-only">{hint}</span> : null}
      {children({ id, describedBy, invalid: Boolean(error) })}
      {error ? (
        <p id={errId} role="alert" style={{ fontSize: fontSize.label, color: "var(--tone-danger-fg)" }}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

type TextInputProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
};

export function TextInput({ label, error, hint, required, ...rest }: TextInputProps) {
  return (
    <Field label={label} error={error} {...(hint ? { hint } : {})} required={Boolean(required)}>
      {({ id, describedBy, invalid }) => (
        <input
          {...rest}
          id={id}
          required={required}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          style={controlStyle(invalid)}
        />
      )}
    </Field>
  );
}

type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
};

export function TextArea({ label, error, hint, required, rows = 4, ...rest }: TextAreaProps) {
  return (
    <Field label={label} error={error} {...(hint ? { hint } : {})} required={Boolean(required)}>
      {({ id, describedBy, invalid }) => (
        <textarea
          {...rest}
          id={id}
          rows={rows}
          required={required}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          style={{ ...controlStyle(invalid), resize: "vertical", minHeight: rows * 22, lineHeight: 1.5 }}
        />
      )}
    </Field>
  );
}

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  children: ReactNode;
};

export function Select({ label, error, hint, required, children, ...rest }: SelectProps) {
  return (
    <Field label={label} error={error} {...(hint ? { hint } : {})} required={Boolean(required)}>
      {({ id, describedBy, invalid }) => (
        <select
          {...rest}
          id={id}
          required={required}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          style={controlStyle(invalid)}
        >
          {children}
        </select>
      )}
    </Field>
  );
}

export function Checkbox({
  label, checked, onChange, error, id: providedId,
}: {
  label: ReactNode; checked: boolean;
  onChange: (v: boolean) => void; error?: string | undefined; id?: string;
}) {
  const generated = useId();
  const id = providedId ?? generated;
  const errId = `${id}-err`;
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.currentTarget.checked)}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errId : undefined}
          style={{ width: 18, height: 18, marginTop: 1, accentColor: "var(--accent)" }}
        />
        <label htmlFor={id} style={{ fontSize: fontSize.body, color: "var(--text-2)", lineHeight: 1.5 }}>
          {label}
        </label>
      </div>
      {error ? (
        <p id={errId} role="alert" style={{ fontSize: fontSize.label, color: "var(--tone-danger-fg)" }}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Form-level error, distinct from a field error. */
export function FormError({ message }: { message: string }) {
  return (
    <div
      role="alert"
      style={{
        display: "flex",
        gap: 10,
        padding: "12px 14px",
        borderRadius: radius.sm,
        background: "var(--tone-danger-bg)",
        color: "var(--tone-danger-fg)",
        fontSize: fontSize.body,
      }}
    >
      {message}
    </div>
  );
}

export function FormNotice({ message }: { message: string }) {
  return (
    <div
      role="status"
      style={{
        display: "flex",
        gap: 10,
        padding: "12px 14px",
        borderRadius: radius.sm,
        background: "var(--tone-ok-bg)",
        color: "var(--tone-ok-fg)",
        fontSize: fontSize.body,
      }}
    >
      {message}
    </div>
  );
}
